import type { EventCategory } from "@/lib/events";
import type { MusicGenre } from "@/lib/musicGenres";
import { MUSIC_GENRE_IDS } from "@/lib/musicGenres";

/** Artist-name keywords — fallback when DB genres column is empty. */
const ARTIST_GENRE_KEYWORDS: Record<MusicGenre, string[]> = {
  "Rock / Metal": [
    "megadeth", "metallica", "slayer", "maiden", "ozzy", "black crowes",
    "rise against", "nirvana", "foo fighters", "punk", "hardcore",
  ],
  "Electronic / Techno": ["techno", "house", "electronic", "trance", "edm", "dubstep", "minimal"],
  "Hip-Hop / R&B": ["hip-hop", "hip hop", "rap", "r&b", "rnb", "trap", "drake", "kendrick"],
  "Jazz / Blues": ["jazz", "blues", "swing", "bebop"],
  "Latin / Salsa": ["salsa", "bachata", "merengue", "cumbia", "bossa nova"],
  Klassik: ["klassik", "classical", "orchestra", "symphonie", "philharmonic", "opera", "chamber"],
  "Indie / Alternative": ["indie", "alternative", "alt-rock", "shoegaze", "two door cinema club", "tash sultana"],
  "Funk / Soul": ["funk", "soul", "motown", "rubén blades"],
  Pop: ["taylor swift", "beyoncé", "bad gyal"],
  "World Music": ["world music", "afrobeat", "ethnic"],
  Comedy: ["comedy", "comedian", "open mic", "stand-up", "standup", "kabarett", "cabaret"],
};

const COMEDY_EVENT_RE =
  /\b(comedy|comedian|open\s*mic|stand[-\s]?up|kabarett|cabaret)\b/i;

const PARTY_GENRE_RULES: { pattern: RegExp; genres: MusicGenre[] }[] = [
  { pattern: COMEDY_EVENT_RE, genres: ["Comedy"] },
  { pattern: /2000er/i, genres: ["Pop", "Hip-Hop / R&B", "Electronic / Techno"] },
  { pattern: /90er/i, genres: ["Pop", "Rock / Metal", "Hip-Hop / R&B"] },
  { pattern: /80er/i, genres: ["Pop", "Rock / Metal", "Funk / Soul"] },
  { pattern: /disco/i, genres: ["Funk / Soul", "Pop"] },
  { pattern: /latin/i, genres: ["Latin / Salsa"] },
  { pattern: /reggaeton/i, genres: ["Latin / Salsa", "Hip-Hop / R&B"] },
  { pattern: /hip.?hop|hiphop/i, genres: ["Hip-Hop / R&B"] },
  { pattern: /techno|house|rave|electronic/i, genres: ["Electronic / Techno"] },
  { pattern: /jazz/i, genres: ["Jazz / Blues"] },
  { pattern: /\brock\b|\bmetal\b/i, genres: ["Rock / Metal"] },
  { pattern: /indie/i, genres: ["Indie / Alternative"] },
  { pattern: /soul|funk/i, genres: ["Funk / Soul"] },
  { pattern: /\bpop\b/i, genres: ["Pop"] },
  { pattern: /trance/i, genres: ["Electronic / Techno"] },
  { pattern: /drum.?n?.?bass/i, genres: ["Electronic / Techno"] },
];

export const DEFAULT_CLUB_GENRES: MusicGenre[] = [
  "Electronic / Techno",
  "Hip-Hop / R&B",
  "Pop",
];

export const DEFAULT_BAR_GENRES: MusicGenre[] = [
  "Pop",
  "Hip-Hop / R&B",
  "Indie / Alternative",
];

const GENERIC_PARTY_HINTS = [
  "party", "partynight", "nacht", "night", "session", "open air", "clubbing",
  "dancefloor", "feier", "fest", "festival", "dj", "rave", "disco",
  "2000er", "90er", "80er",
];

const ARTIST_KEYWORD_ORDER: MusicGenre[] = [
  "Comedy", "Rock / Metal", "Electronic / Techno", "Hip-Hop / R&B", "Jazz / Blues",
  "Latin / Salsa", "Klassik", "Indie / Alternative", "Funk / Soul", "World Music", "Pop",
];

function looksLikeComedy(eventName: string, description: string): boolean {
  return COMEDY_EVENT_RE.test(`${eventName} ${description}`);
}

const LEGACY_GENRE_MAP: Record<string, MusicGenre> = {
  Techno: "Electronic / Techno",
  House: "Electronic / Techno",
  "Drum & Bass": "Electronic / Techno",
  Disco: "Funk / Soul",
  "Hip-Hop": "Hip-Hop / R&B",
  Jazz: "Jazz / Blues",
  "Live Music": "World Music",
};

function uniqueGenres(genres: MusicGenre[]): MusicGenre[] {
  return [...new Set(genres)];
}

function matchesKeyword(text: string, keyword: string): boolean {
  if (keyword.includes(" ")) return text.includes(keyword);
  const re = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
  return re.test(text);
}

function looksGenericLabel(text: string): boolean {
  const lower = text.toLowerCase();
  return GENERIC_PARTY_HINTS.some((hint) => lower.includes(hint));
}

function matchPartyGenres(eventName: string): MusicGenre[] {
  const matched: MusicGenre[] = [];
  for (const { pattern, genres } of PARTY_GENRE_RULES) {
    if (pattern.test(eventName)) matched.push(...genres);
  }
  return uniqueGenres(matched);
}

function matchArtistGenres(eventName: string, description: string): MusicGenre[] {
  const text = `${eventName} ${description}`.toLowerCase();
  const matched: MusicGenre[] = [];
  for (const genre of ARTIST_KEYWORD_ORDER) {
    if (ARTIST_GENRE_KEYWORDS[genre].some((kw) => matchesKeyword(text, kw))) {
      matched.push(genre);
    }
  }
  return uniqueGenres(matched);
}

function extractArtistName(eventName: string): string | null {
  const trimmed = eventName.trim();
  if (!trimmed) return null;
  const split = trimmed.split(/\s[-–—|@]\s/);
  const candidate = split[0]?.trim();
  return candidate && candidate.length >= 2 ? candidate : null;
}

function hasSpecificArtist(eventName: string, category: EventCategory): boolean {
  if (matchPartyGenres(eventName).length > 0) return false;
  if (looksGenericLabel(eventName)) return false;
  if (/\s[-–—|@]\s/.test(eventName)) {
    const artist = extractArtistName(eventName);
    if (artist && !looksGenericLabel(artist)) return true;
  }
  if (category === "Concerts") return !looksGenericLabel(eventName);
  return false;
}

function mapLegacyDbStyle(raw: string | null | undefined): MusicGenre[] | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  if ((MUSIC_GENRE_IDS as readonly string[]).includes(trimmed)) {
    return [trimmed as MusicGenre];
  }
  const legacy = LEGACY_GENRE_MAP[trimmed];
  return legacy ? [legacy] : null;
}

function categoryDefaults(
  category?: EventCategory,
  eventName = "",
  description = "",
): MusicGenre[] {
  if (looksLikeComedy(eventName, description)) return ["Comedy"];
  switch (category) {
    case "Clubs":
      return DEFAULT_CLUB_GENRES;
    case "Bars":
    case "Rooftop":
      return DEFAULT_BAR_GENRES;
    case "Concerts":
      return ["Pop"];
    default:
      return DEFAULT_CLUB_GENRES;
  }
}

/** Local fallback when crawler has not populated genres yet. */
export function getEventGenresSync(
  eventName: string,
  description: string,
  options?: { category?: EventCategory; dbMusicStyle?: string | null },
): MusicGenre[] {
  if (looksLikeComedy(eventName, description)) return ["Comedy"];

  const fromDb = mapLegacyDbStyle(options?.dbMusicStyle);
  if (fromDb) return fromDb;

  const category = options?.category;
  if (hasSpecificArtist(eventName, category ?? "Bars")) {
    const artistGenres = matchArtistGenres(eventName, description);
    if (artistGenres.length > 0) return artistGenres;
    return categoryDefaults(category, eventName, description);
  }

  const partyGenres = matchPartyGenres(eventName);
  if (partyGenres.length > 0) return partyGenres;

  return categoryDefaults(category, eventName, description);
}

/** Read genres from Supabase; fall back to sync mapping for legacy rows. */
export function parseEventGenres(
  stored: string[] | null | undefined,
  eventName: string,
  description: string,
  category: EventCategory,
  dbMusicStyle?: string | null,
): MusicGenre[] {
  const valid = (stored ?? []).filter((g): g is MusicGenre =>
    (MUSIC_GENRE_IDS as readonly string[]).includes(g),
  );
  if (valid.length > 0) return uniqueGenres(valid);
  return getEventGenresSync(eventName, description, { category, dbMusicStyle });
}

export function normalizeMusicStyleTag(tag: string): MusicGenre {
  const trimmed = tag.trim();
  if ((MUSIC_GENRE_IDS as readonly string[]).includes(trimmed)) {
    return trimmed as MusicGenre;
  }
  return LEGACY_GENRE_MAP[trimmed] ?? "Electronic / Techno";
}

export function genresMatchFilter(
  eventGenres: MusicGenre[],
  selectedGenres: Set<MusicGenre>,
): boolean {
  if (selectedGenres.size === 0) return true;
  return eventGenres.some((genre) => selectedGenres.has(genre));
}

/** True when a genre pill adds info beyond category defaults (clubs/bars). */
export function isDisplayableEventGenre(
  genre: string,
  category: EventCategory,
): boolean {
  if (!(MUSIC_GENRE_IDS as readonly string[]).includes(genre)) return false;
  const known = genre as MusicGenre;
  if (category === "Clubs") return !DEFAULT_CLUB_GENRES.includes(known);
  if (category === "Bars") return !DEFAULT_BAR_GENRES.includes(known);
  return true;
}
